import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";

import { useShouldPresentBookingAsLockedRequest_booking$key } from "./__generated__/useShouldPresentBookingAsLockedRequest_booking.graphql";

type RequestWindowBookingRuleEdge =
  | {
      readonly liftAt?: string | null;
      readonly requestCloses?: string | null;
      readonly node?:
        | {
            readonly __typename?: string;
            readonly id?: string;
            readonly requestConfig?: { readonly id: string } | null;
          }
        | null
        | undefined;
    }
  | null
  | undefined;

type RequestConfigRequestWindowArgs = {
  requestConfigs: boolean;
  requestBookingRuleId: string | null | undefined;
  requestBookingRuleRequestConfigId: string | null | undefined;
  requestWindowBookingRuleEdges:
    | readonly RequestWindowBookingRuleEdge[]
    | null
    | undefined;
};

const getMatchingUnpublishedRequestConfigRuleEdge = ({
  requestConfigs,
  requestBookingRuleId,
  requestBookingRuleRequestConfigId,
  requestWindowBookingRuleEdges,
}: RequestConfigRequestWindowArgs) => {
  if (
    !requestConfigs ||
    !requestBookingRuleId ||
    !requestBookingRuleRequestConfigId
  ) {
    return null;
  }

  const requestWindowBookingRuleEdge = requestWindowBookingRuleEdges?.find(
    (edge) =>
      edge?.node?.__typename === "RequestRule" &&
      edge.node.id === requestBookingRuleId &&
      edge.node.requestConfig?.id === requestBookingRuleRequestConfigId
  );

  return requestWindowBookingRuleEdge?.liftAt == null
    ? requestWindowBookingRuleEdge
    : null;
};

export const shouldPresentBookingAsLockedRequest = (
  args: RequestConfigRequestWindowArgs
) => {
  return !!getMatchingUnpublishedRequestConfigRuleEdge(args);
};

export const isRequestConfigRequestWindowClosed = (
  args: RequestConfigRequestWindowArgs,
  nowMs: number = Date.now()
) => {
  const requestWindowBookingRuleEdge =
    getMatchingUnpublishedRequestConfigRuleEdge(args);

  const requestClosesMs = requestWindowBookingRuleEdge?.requestCloses
    ? Date.parse(requestWindowBookingRuleEdge.requestCloses)
    : null;

  return (
    requestClosesMs !== null &&
    !Number.isNaN(requestClosesMs) &&
    requestClosesMs < nowMs
  );
};

export const useShouldPresentBookingAsLockedRequest = (
  bookingRef: useShouldPresentBookingAsLockedRequest_booking$key | null
) => {
  const booking = useFragment(
    useShouldPresentBookingAsLockedRequestFragmentString,
    bookingRef
  );
  const { requestConfigs } = useFlags();
  const requestBookingRule = booking?.request?.bookingRule;
  const requestBookingRuleRequestConfigId =
    requestBookingRule?.__typename === "RequestRule"
      ? requestBookingRule.requestConfig?.id
      : null;

  return shouldPresentBookingAsLockedRequest({
    requestConfigs: !!requestConfigs,
    requestBookingRuleId: requestBookingRule?.id,
    requestBookingRuleRequestConfigId,
    requestWindowBookingRuleEdges:
      booking?.startSlot?.agenda?.configuredRequestRestrictions?.edges,
  });
};

export const useIsRequestConfigRequestWindowClosed = (
  bookingRef: useShouldPresentBookingAsLockedRequest_booking$key | null
) => {
  const booking = useFragment(
    useShouldPresentBookingAsLockedRequestFragmentString,
    bookingRef
  );
  const { requestConfigs } = useFlags();
  const requestBookingRule = booking?.request?.bookingRule;
  const requestBookingRuleRequestConfigId =
    requestBookingRule?.__typename === "RequestRule"
      ? requestBookingRule.requestConfig?.id
      : null;

  return isRequestConfigRequestWindowClosed({
    requestConfigs: !!requestConfigs,
    requestBookingRuleId: requestBookingRule?.id,
    requestBookingRuleRequestConfigId,
    requestWindowBookingRuleEdges:
      booking?.startSlot?.agenda?.configuredRequestRestrictions?.edges,
  });
};

const useShouldPresentBookingAsLockedRequestFragmentString = graphql`
  fragment useShouldPresentBookingAsLockedRequest_booking on Booking {
    startSlot {
      agenda {
        configuredRequestRestrictions: requestWindowBookingRules(
          type: [REQUEST]
        ) {
          edges {
            liftAt
            requestCloses
            node {
              id
              __typename
              ... on RequestRule {
                requestConfig {
                  id
                }
              }
            }
          }
        }
      }
    }
    ... on StandardBooking {
      request {
        bookingRule {
          id
          __typename
          ... on RequestRule {
            requestConfig {
              id
            }
          }
        }
      }
    }
    ... on LessonBooking {
      request {
        bookingRule {
          id
          __typename
          ... on RequestRule {
            requestConfig {
              id
            }
          }
        }
      }
    }
  }
`;
